import * as React from 'react';
import {
  createRoute,
  useNavigate,
  useParams,
  useSearch,
  type AnyRoute,
  type RouteComponent,
} from '@tanstack/react-router';
import type { FieldValues } from 'react-hook-form';
import { Button } from '../primitives/button.js';
import { Trash2 } from 'lucide-react';
import { ResourceDetail } from './detail/resource-detail.js';
import { ResourceForm } from './form/resource-form.js';
import { ResourceList } from './list/resource-list.js';
import { isForbidden } from './client.js';
import { defaultListState, listSearchSchema } from './list-state.js';
import { useResourceItem, useResourceMutations } from './queries.js';
import { useConfirm } from './confirm.js';
import type { ListState, ResourceDefinition, ResourcePermissions } from './types.js';

export interface ResourceRoutesOptions<
  TItem,
  TValues extends Record<string, unknown> & FieldValues,
  TPath extends string,
  TCreate = TValues,
  TUpdate = Partial<TValues>,
> {
  resource: ResourceDefinition<TItem, TValues, TCreate, TUpdate>;
  getParentRoute: () => AnyRoute;
  /**
   * Route segment, e.g. `contatti`. The kit appends `/nuovo`, `/$id` and
   * `/$id/modifica`. Kept as a literal type so `<Link to="/contatti">` is
   * checked against the routes this call actually creates.
   */
  path: TPath;
  /** Evaluated per render, so abilities can come from a session hook. */
  usePermissions?: () => ResourcePermissions;
  /**
   * Extra controls beside the list's own toolbar — the zone import form lands
   * here. A hook rather than a node so it can hold state of its own without the
   * route re-rendering the whole list around it.
   */
  useListToolbarExtra?: () => React.ReactNode;
}

const ALL_ALLOWED: ResourcePermissions = { read: true, create: true, update: true, remove: true };

/**
 * `strict: false` is how a generic factory reads route state it cannot name at
 * definition time, and it returns `any`. These two helpers are the only place
 * that untyped edge is held; everything downstream is typed again.
 */
function useListSearch(): Partial<ListState> {
  const search: unknown = useSearch({ strict: false });
  return search as Partial<ListState>;
}

function useIdParam(): string {
  const params = useParams({ strict: false }) as unknown as { id?: string };
  return params.id ?? '';
}

/**
 * Navigation options for a route the factory builds at runtime.
 *
 * The router types `to` against the routes it knows about, which a generic
 * factory has not created yet at the point its components are written. The
 * paths here are the ones this very call registers, so the loss is contained.
 */
type LooseNavigate = (options: {
  to: string;
  search?: unknown;
  replace?: boolean;
  resetScroll?: boolean;
}) => Promise<void>;

/**
 * How a list's own state — search, filters, page, sort — is written to the URL.
 *
 * Replaced rather than pushed, so Back leaves the list instead of undoing one
 * keystroke; and without the router's reset to the top, because the list may
 * sit below the fold (the Agenda's sits under its calendar), and a search that
 * lands in the URL a moment after it was typed would otherwise pull the page
 * away from whoever is reading it, or from a row menu they just opened.
 */
export const LIST_STATE_NAVIGATION = { replace: true, resetScroll: false } as const;

/**
 * List, detail, create and edit routes generated from one declaration.
 *
 * View state lives in the URL: pagination, sort, search and filters are search
 * params validated by `listSearchSchema`, so every view is linkable, back and
 * forward behave, and a reload lands the user where they were.
 */
export function createResourceRoutes<
  TItem,
  TValues extends Record<string, unknown> & FieldValues,
  const TPath extends string,
  TCreate = TValues,
  TUpdate = Partial<TValues>,
>({
  resource,
  getParentRoute,
  path,
  usePermissions,
  useListToolbarExtra,
}: ResourceRoutesOptions<TItem, TValues, TPath, TCreate, TUpdate>) {
  const usePerms = usePermissions ?? (() => ALL_ALLOWED);
  const listPath = `/${path}` as const;

  /** No mapper means the form is edited in the wire shape. */
  const toInput = (values: TValues): TCreate =>
    resource.form?.toInput ? resource.form.toInput(values) : (values as unknown as TCreate);

  const listRoute = createRoute({
    getParentRoute,
    path: listPath,
    validateSearch: (input: Record<string, unknown>) => listSearchSchema.parse(input),
    component: (() => {
      const search = useListSearch();
      // The router cannot type search params for a route shape it only learns
      // at call time; the state is validated by `listSearchSchema` instead.
      const navigate = useNavigate() as unknown as LooseNavigate;
      const permissions = usePerms();
      const toolbarExtra = useListToolbarExtra?.();

      const state = React.useMemo<ListState>(
        () => ({ ...defaultListState(resource.defaultState), ...search }),
        [search],
      );

      return (
        <ResourceList
          resource={resource}
          state={state}
          {...(toolbarExtra ? { overrides: { toolbarExtra } } : {})}
          onStateChange={(next) => {
            void navigate({ to: listPath, search: next, ...LIST_STATE_NAVIGATION });
          }}
          canRead={permissions.read ?? true}
          canCreate={permissions.create ?? false}
          onCreate={() => void navigate({ to: `${listPath}/nuovo` })}
          onOpen={(row) => void navigate({ to: `${listPath}/${resource.getRowId(row)}` })}
        />
      );
    }) as RouteComponent,
  });

  const newRoute = createRoute({
    getParentRoute,
    path: `${listPath}/nuovo`,
    // Anything may be in the query string; the resource's `fromSearch` decides
    // what of it is a field. Unknown keys are carried and ignored.
    validateSearch: (input: Record<string, unknown>): Record<string, string> =>
      Object.fromEntries(
        Object.entries(input).flatMap(([key, value]) =>
          typeof value === 'string' ? [[key, value] as const] : [],
        ),
      ),
    component: (() => {
      const navigate = useNavigate() as unknown as LooseNavigate;
      const mutations = useResourceMutations<TItem, TCreate, TUpdate>(resource);
      const search: Record<string, string> = useSearch({ strict: false });

      if (!resource.form) return null;

      const prefilled = resource.form.fromSearch?.(search);

      return (
        <ResourceForm<TValues>
          formKey={`${resource.key}:new`}
          spec={resource.form}
          initialValues={
            prefilled && Object.keys(prefilled).length > 0
              ? { ...resource.form.defaultValues, ...prefilled }
              : undefined
          }
          title={`${resource.label.gender === 'f' ? 'Nuova' : 'Nuovo'} ${resource.label.one.toLowerCase()}`}
          onCancel={() => void navigate({ to: listPath })}
          onSubmit={async (values) => {
            const created = await mutations.create.mutateAsync(toInput(values));
            await navigate({ to: `${listPath}/${resource.getRowId(created)}` });
          }}
        />
      );
    }) as RouteComponent,
  });

  const detailRoute = createRoute({
    getParentRoute,
    path: `${listPath}/$id`,
    component: (() => {
      const id = useIdParam();
      const navigate = useNavigate() as unknown as LooseNavigate;
      const permissions = usePerms();
      const query = useResourceItem(resource, id);
      const mutations = useResourceMutations<TItem, TCreate, TUpdate>(resource);
      const { confirm, dialog } = useConfirm();

      if (!resource.detail) return null;

      return (
        <>
          <ResourceDetail
            spec={resource.detail}
            item={query.data}
            isPending={query.isPending}
            error={query.isError && !isForbidden(query.error) ? query.error : undefined}
            forbidden={query.isError && isForbidden(query.error)}
            onRetry={() => void query.refetch()}
            actions={
              <>
                {(permissions.update ?? false) && resource.form && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void navigate({ to: `${listPath}/${id}/modifica` })}
                  >
                    Modifica
                  </Button>
                )}
                {(permissions.remove ?? false) && resource.client.remove && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      confirm(
                        {
                          title: `Eliminare ${
                            resource.label.gender === 'f' ? 'questa' : 'questo'
                          } ${resource.label.one.toLowerCase()}?`,
                          description: 'L’operazione non può essere annullata.',
                          confirmLabel: 'Elimina',
                        },
                        'danger',
                        async () => {
                          await mutations.remove.mutateAsync(id);
                          await navigate({ to: listPath });
                        },
                      )
                    }
                  >
                    <Trash2 />
                    Elimina
                  </Button>
                )}
              </>
            }
          />
          {dialog}
        </>
      );
    }) as RouteComponent,
  });

  const editRoute = createRoute({
    getParentRoute,
    path: `${listPath}/$id/modifica`,
    component: (() => {
      const id = useIdParam();
      const navigate = useNavigate() as unknown as LooseNavigate;
      const query = useResourceItem(resource, id);
      const mutations = useResourceMutations<TItem, TCreate, TUpdate>(resource);

      if (!resource.form) return null;
      const form = resource.form;

      const initialValues = query.data
        ? (form.fromItem?.(query.data) ?? (query.data as unknown as TValues))
        : undefined;

      return (
        <ResourceForm<TValues>
          formKey={`${resource.key}:edit`}
          spec={form}
          initialValues={initialValues}
          title={`Modifica ${resource.label.one.toLowerCase()}`}
          onCancel={() => void navigate({ to: `${listPath}/${id}` })}
          onSubmit={async (values) => {
            await mutations.update.mutateAsync({
              id,
              input: toInput(values) as unknown as TUpdate,
            });
            await navigate({ to: `${listPath}/${id}` });
          }}
        />
      );
    }) as RouteComponent,
  });

  return { listRoute, newRoute, detailRoute, editRoute };
}
